import { useState, useEffect, useCallback } from 'react';
import { Lightbulb, ChevronUp, Plus, X, MessageSquarePlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const CATEGORIES = ['general', 'mapping', 'intelligence', 'integrations', 'ui'] as const;

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  vote_count: number;
  created_at: string;
  user_id: string;
}

export function FeatureRequestWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [myVotes, setMyVotes] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('general');
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState<'votes' | 'newest'>('votes');

  const fetchData = useCallback(async () => {
    if (!user) return;
    const [{ data: reqs }, { data: votes }] = await Promise.all([
      supabase.from('feature_requests' as any).select('*').order(
        sortBy === 'votes' ? 'vote_count' : 'created_at',
        { ascending: false }
      ).limit(50),
      supabase.from('feature_votes' as any).select('feature_id').eq('user_id', user.id),
    ]);
    setRequests((reqs as any[] ?? []) as FeatureRequest[]);
    setMyVotes(new Set((votes as any[] ?? []).map((v: any) => v.feature_id)));
  }, [user, sortBy]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const handleSubmit = async () => {
    if (!user || !title.trim()) return;
    setSubmitting(true);
    const { error } = await supabase.from('feature_requests' as any).insert({
      user_id: user.id,
      title: title.trim().slice(0, 200),
      description: description.trim().slice(0, 1000),
      category,
    } as any);
    setSubmitting(false);
    if (error) { toast.error('Failed to submit'); return; }
    toast.success('Feature request submitted!');
    setTitle(''); setDescription(''); setShowForm(false);
    fetchData();
  };

  const toggleVote = async (featureId: string) => {
    if (!user) return;
    const hasVoted = myVotes.has(featureId);
    if (hasVoted) {
      await supabase.from('feature_votes' as any).delete().eq('feature_id', featureId).eq('user_id', user.id);
      setMyVotes(prev => { const n = new Set(prev); n.delete(featureId); return n; });
      setRequests(prev => prev.map(r => r.id === featureId ? { ...r, vote_count: r.vote_count - 1 } : r));
    } else {
      await supabase.from('feature_votes' as any).insert({ feature_id: featureId, user_id: user.id } as any);
      setMyVotes(prev => new Set(prev).add(featureId));
      setRequests(prev => prev.map(r => r.id === featureId ? { ...r, vote_count: r.vote_count + 1 } : r));
    }
  };

  if (!user) return null;

  return (
    <>
      {/* FAB trigger */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-[80px] right-[22px] z-40 h-10 w-10 rounded-full bg-accent text-accent-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center ring-2 ring-background"
        title="Feature Requests"
        aria-label="Open feature requests"
      >
        <Lightbulb className="h-5 w-5" />
      </button>

      {/* Panel */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setOpen(false)} />
          <div className="fixed right-4 bottom-4 w-[380px] max-h-[80vh] bg-card border border-border rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Feature Requests</h3>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowForm(f => !f)}
                  className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center transition-colors"
                  title="New request"
                >
                  {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* New request form */}
            {showForm && (
              <div className="px-4 py-3 border-b border-border space-y-2.5 bg-muted/10">
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Feature title…"
                  maxLength={200}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe what you'd like and why…"
                  maxLength={1000}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
                <div className="flex items-center gap-2">
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !title.trim()}
                    className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                </div>
              </div>
            )}

            {/* Sort tabs */}
            <div className="flex items-center gap-1 px-4 py-2 border-b border-border">
              {(['votes', 'newest'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setSortBy(s)}
                  className={cn(
                    "px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors",
                    sortBy === s ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {s === 'votes' ? 'Top Voted' : 'Newest'}
                </button>
              ))}
              <span className="ml-auto text-[10px] text-muted-foreground">{requests.length} ideas</span>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {requests.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                  <MessageSquarePlus className="h-8 w-8 text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">No feature requests yet.</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Be the first to suggest an idea!</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {requests.map(req => {
                    const voted = myVotes.has(req.id);
                    const isOwn = req.user_id === user.id;
                    return (
                      <div key={req.id} className="flex gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
                        {/* Vote button */}
                        <button
                          onClick={() => toggleVote(req.id)}
                          className={cn(
                            "flex flex-col items-center gap-0.5 min-w-[40px] py-1.5 rounded-lg border transition-all text-xs font-semibold",
                            voted
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:border-primary/50 hover:text-primary"
                          )}
                        >
                          <ChevronUp className={cn("h-3.5 w-3.5", voted && "text-primary")} />
                          {req.vote_count}
                        </button>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-foreground leading-snug">{req.title}</p>
                          {req.description && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{req.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                              {req.category}
                            </span>
                            {req.status !== 'open' && (
                              <span className={cn(
                                "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                                req.status === 'planned' && "bg-blue-500/10 text-blue-600 dark:text-blue-400",
                                req.status === 'in_progress' && "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                                req.status === 'done' && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                              )}>
                                {req.status.replace('_', ' ')}
                              </span>
                            )}
                            {isOwn && (
                              <span className="text-[10px] text-muted-foreground/50">yours</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
